import { formatDateShort, formatDateTime, formatPrice } from '@/lib/format';
import {
  formatTravelEsimData,
  formatTravelEsimDuration,
  formatTravelEsimPricePerDay,
  travelEsimLineStatus,
  travelEsimStatusClasses,
} from '@/lib/travel-esim';
import type { OrderItem } from '@octave/contracts';
import { QrCode } from 'lucide-react';
import { useState } from 'react';
import { TravelEsimQrCodeDialog } from './TravelEsimQrCodeDialog';

interface TravelEsimItemMetadataProps {
  item: OrderItem;
  /** Needed to open the QR code (the item id alone does not identify it). */
  orderId?: string;
  /** Octave admins can preview a QR that was not sent yet. */
  canPreviewUnsent?: boolean;
  /** Octave admin view: the grid plans to buy for this eSIM. */
  showComponents?: boolean;
}

/** Order-line details of an international eSIM (snapshot taken at cart time). */
export function TravelEsimItemMetadata({
  item,
  orderId,
  canPreviewUnsent = false,
  showComponents = false,
}: TravelEsimItemMetadataProps) {
  const { metadata } = item;
  const [showQrCode, setShowQrCode] = useState(false);

  if (metadata.type !== 'TRAVEL_ESIM') {
    return null;
  }

  const { beneficiary } = metadata;
  const status = travelEsimLineStatus(metadata);

  return (
    <div className="mt-2 space-y-3">
      <div className="space-y-0.5 text-sm text-slate-600">
        <div>Destination: {metadata.country}</div>
        <div>
          Forfait: {formatTravelEsimData(metadata.dataLimitGb)} pendant{' '}
          {formatTravelEsimDuration(metadata.stayDurationDays)}, soit{' '}
          {formatTravelEsimPricePerDay(metadata.pricePerDayCents)}
        </div>
        <div>
          Séjour: du {formatDateShort(metadata.departureDate)} au{' '}
          {formatDateShort(metadata.returnDate)}
        </div>
        {showComponents && (
          <div className="text-slate-700">
            À acheter:{' '}
            {metadata.components
              .map(
                (c) =>
                  `${c.quantity} × ${formatTravelEsimDuration(c.durationDays)} (${formatPrice(c.priceCents)})`,
              )
              .join(' + ')}{' '}
            · couvre {formatTravelEsimDuration(metadata.coveredDays)}
          </div>
        )}
        <div>
          Bénéficiaire: {beneficiary.name}
          {beneficiary.kind === 'LINE' ? ` (${beneficiary.lineNumber})` : ''}
        </div>
        <div>
          {metadata.orderKind === 'RECHARGE'
            ? `Recharge de l'eSIM existante${metadata.esimNumber ? ` n° ${metadata.esimNumber}` : ''}`
            : 'Nouvelle eSIM'}
        </div>
        <div>E-mail du QR code: {beneficiary.email}</div>
        {metadata.esimNumber && metadata.orderKind !== 'RECHARGE' && (
          <div>
            Numéro eSIM:{' '}
            <span className="font-mono text-slate-700">{metadata.esimNumber}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700">
          {beneficiary.kind === 'LINE' ? 'Membre de la flotte' : 'Hors flotte'}
        </span>
        {metadata.siteName && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
            {metadata.siteName}
          </span>
        )}
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${travelEsimStatusClasses(status.tone)}`}
        >
          {statusLabel(metadata)}
        </span>
        {metadata.qrCodePath &&
          orderId &&
          (metadata.qrCodeSentAt || canPreviewUnsent) && (
          <button
            type="button"
            onClick={() => setShowQrCode(true)}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-900 text-white hover:bg-slate-700 cursor-pointer"
          >
            <QrCode className="h-3 w-3" />
            Voir le QR code
          </button>
        )}
      </div>

      {showQrCode && orderId && (
        <TravelEsimQrCodeDialog
          orderId={orderId}
          itemId={item.id}
          title={`${metadata.country} · ${beneficiary.name}`}
          onClose={() => setShowQrCode(false)}
        />
      )}
    </div>
  );
}

type TravelEsimMetadata = Extract<OrderItem['metadata'], { type: 'TRAVEL_ESIM' }>;

/** The status pill, with the date when something was sent or loaded. */
function statusLabel(metadata: TravelEsimMetadata): string {
  const status = travelEsimLineStatus(metadata);
  if (metadata.orderKind === 'RECHARGE' && metadata.planLoadedAt) {
    return `${status.label} le ${formatDateTime(metadata.planLoadedAt)}`;
  }
  if (metadata.orderKind !== 'RECHARGE' && metadata.qrCodeSentAt) {
    return `${status.label} le ${formatDateTime(metadata.qrCodeSentAt)}`;
  }
  return status.label;
}
